import React from 'react'
import { requestBlob, showAxiosErrorAsToast, useRequest } from '../../request'
import { Tooltip } from '../../components/simple/tooltip'
import { IconPerson } from '../../icons/person'
import { DonutChart, PieData } from '../../components/charts/pieChart'
import {
  formatDateRange,
  PopupDateRangePicker,
  SimpleDateRange,
  SimpleDateRangePicker,
} from '../../components/form/datePicker'
import { IconHome, IconSchool, IconSort, IconTimes } from '../../icons/library'
import { SimpleUser, useAppContext } from '../../providers/context'
import { UserCard } from '../../components/userCard'
import { ProgressBar } from '../../components/simple/progressBar'
import { Link } from 'react-router-dom'
import { Badge, BadgeStyle } from '../../components/simple/badge'
import { downloadFile } from '../../utils'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { LineChart, LineChartData } from '../../components/charts/lineChart'
import { DateRange } from 'react-day-picker'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import assert from 'assert'
import { Select } from '../../components/form/select'
import { CharcoalInput, TextInput } from '../../components/form/textInput'

const WIDGET_SIZE = 300

export const Widget: React.FC<{
  children: React.ReactNode
  className?: string
  loading?: boolean
  size?: [number, number]
}> = ({ children, className, loading, size = [1, 1] }) => {
  return (
    <div
      className={`flex flex-col text-nowrap rounded-xl ${className?.includes('bg-') ? '' : 'bg-white/50'} px-6 pb-6 pt-5 shadow-lg ${className} ${loading ? 'shimmer *:opacity-50' : ''}`}
      style={{
        minWidth: `${size[0] * WIDGET_SIZE + Math.max(size[0] - 1, 0) * 21}px`,
        minHeight: `${size[1] * WIDGET_SIZE + Math.max(size[1] - 1, 0) * 21}px`,
        maxWidth: `${size[0] * WIDGET_SIZE + Math.max(size[0] - 1, 0) * 21}px`,
        maxHeight: `${size[1] * WIDGET_SIZE + Math.max(size[1] - 1, 0) * 21}px`,
      }}
    >
      {children}
    </div>
  )
}

export const WidgetRangePicker: React.FC<{
  range: SimpleDateRange
  onRangeChange: (range: SimpleDateRange) => void
}> = ({ range, onRangeChange }) => (
  <SimpleDateRangePicker
    range={range}
    onRangeChange={onRangeChange}
    className="!border-none bg-transparent lowercase text-black/50 !ring-0"
    outerClasses="-mr-2"
    arrow={<IconSort size="xs" className="mt-0.5 text-black/40" />}
  />
)

export const UsersWidget: React.FC<{
  schoolID?: number
}> = ({ schoolID }) => {
  const { data, loading } = useRequest<{ admin: number; staff: number; guardians: number; students: number }>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'users',
        schoolID,
      }),
      [schoolID]
    )
  )

  const [showAdminCount, setShowAdminCount] = React.useState(false)
  const [showStaffCount, setShowStaffCount] = React.useState(false)
  const [showGuardianCount, setShowGuardianCount] = React.useState(false)
  const [showStudentCount, setShowStudentCount] = React.useState(false)

  const total = data ? data.admin + data.staff + data.guardians + data.students : 0
  const totalIcons = 114
  const adminIcons = Math.ceil(((data?.admin || 0) * totalIcons) / total)
  const staffIcons = Math.ceil(((data?.staff || 0) * totalIcons) / total)
  const guardianIcons = Math.ceil(((data?.guardians || 0) * totalIcons) / total)
  const studentIcons = totalIcons - adminIcons - staffIcons - guardianIcons

  return (
    <Widget loading={!data || loading} className="bg-warning">
      <div className="text-[15px] font-bold text-black/50">Users</div>
      {data && (
        <>
          <div className="text-4xl font-[500]">{total}</div>
          <div className="mt-2 flex flex-col rounded-md bg-white p-2">
            <div className="flex justify-between font-bold">
              <Tooltip open={showAdminCount || undefined} text={`${data.admin} Admins`}>
                <div className="text-logo">Admin</div>
              </Tooltip>
              <Tooltip open={showStaffCount || undefined} text={`${data.staff} Staff`}>
                <div className="text-warning">Staff</div>
              </Tooltip>
              <Tooltip open={showGuardianCount || undefined} text={`${data.guardians} Guardians`}>
                <div className="text-text">Guardians</div>
              </Tooltip>
              <Tooltip open={showStudentCount || undefined} text={`${data.students} Students`}>
                <div className="text-link">Students</div>
              </Tooltip>
            </div>
            <div className="flex flex-wrap">
              {Array.from({ length: adminIcons }).map((_, i) => (
                <IconPerson
                  key={i}
                  className="-mb-[0.1rem] -mr-[0.1rem] scale-y-[0.87] pb-[0.16rem] pl-1 pr-[0.08rem] text-[180%] text-logo"
                  onHoverChange={setShowAdminCount}
                />
              ))}
              {Array.from({ length: staffIcons }).map((_, i) => (
                <IconPerson
                  key={i}
                  className="-mb-[0.1rem] -mr-[0.1rem] scale-y-[0.87] pb-[0.16rem] pl-1 pr-[0.08rem] text-[180%] text-warning"
                  onHoverChange={setShowStaffCount}
                />
              ))}
              {Array.from({ length: guardianIcons }).map((_, i) => (
                <IconPerson
                  key={i}
                  className="-mb-[0.1rem] -mr-[0.1rem] scale-y-[0.87] pb-[0.16rem] pl-1 pr-[0.08rem] text-[180%] text-text"
                  onHoverChange={setShowGuardianCount}
                />
              ))}
              {Array.from({ length: studentIcons }).map((_, i) => (
                <IconPerson
                  key={i}
                  className="-mb-[0.1rem] -mr-[0.1rem] scale-y-[0.87] pb-[0.16rem] pl-1 pr-[0.08rem] text-[180%] text-link"
                  onHoverChange={setShowStudentCount}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </Widget>
  )
}

export const CommunicationsWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const [range, setRange] = React.useState<SimpleDateRange>('month')

  const { data, loading } = useRequest<{ translated: number; total: number; pie: PieData }>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'communications',
        schoolID,
        data: {
          range: formatDateRange(range),
        },
      }),
      [schoolID, range]
    )
  )

  return (
    <Widget loading={!data || loading}>
      {data && (
        <>
          <div className="flex items-center justify-between">
            <div className="font-bold text-black/50">Total Communications</div>
            <WidgetRangePicker range={range} onRangeChange={setRange} />
          </div>
          {data && (
            <>
              <div className="text-4xl">{data.total}</div>
              <DonutChart className="mt-auto" data={data.pie} legend={{ side: 'left', className: 'text-xs mb-12' }} />
            </>
          )}
        </>
      )}
    </Widget>
  )
}

export const TopUsersWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const { data, loading } = useRequest<(SimpleUser & { sent: number; received: number })[]>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'ranking',
        schoolID,
      }),
      [schoolID]
    )
  )
  return (
    <Widget size={[1, 2]} className="bg-link" loading={!data || loading}>
      <div className="mb-4 font-bold text-white/75">Top Users in the last month</div>
      {data && (
        <div className="flex flex-grow flex-col justify-between">
          {data.map((user) => (
            <div className="flex flex-col overflow-hidden rounded-lg bg-white px-4 py-3 shadow-sm" key={user.id}>
              <UserCard user={user} key={user.id} />
              <div className="mt-1">
                <b>{user.received}</b> messages received, <b>{user.sent}</b> sent
              </div>
            </div>
          ))}
        </div>
      )}
    </Widget>
  )
}

export const EngagementWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const [range, setRange] = React.useState<SimpleDateRange>('all')

  const { data, loading } = useRequest<{
    staff: { sending: string; receiving: string }
    families: { sending: string; receiving: string }
    multilingual: { sending: string; receiving: string }
  }>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'engagement',
        schoolID,
        data: {
          range: formatDateRange(range),
        },
      }),
      [schoolID, range]
    )
  )

  return (
    <Widget loading={!data || loading}>
      {data && (
        <>
          <div className="flex items-center justify-between">
            <div className="font-bold text-black/50">Engagement</div>
            <WidgetRangePicker range={range} onRangeChange={setRange} />
          </div>
          <div className="mt-1 font-[500] text-black/50">Staff</div>
          <div className="text-sm text-black/40">sending messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-rose-600"
            percentage={data.staff.sending}
            tooltip={`${data.staff.sending} of staff sent messages`}
          />
          <div className="text-sm text-black/40">receiving messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-violet-700"
            percentage={data.staff.receiving}
            tooltip={`${data.staff.receiving} of staff received messages`}
          />
          <div className="mt-2 font-[500] text-black/50">Families</div>
          <div className="text-sm text-black/40">sending messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-rose-600"
            percentage={data.families.sending}
            tooltip={`${data.families.sending} of families sent messages`}
          />
          <div className="text-sm text-black/40">receiving messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-violet-700"
            percentage={data.families.receiving}
            tooltip={`${data.families.receiving} of families received messages`}
          />
          <div className="mt-2 font-[500] text-black/50">Multilingual</div>
          <div className="text-sm text-black/40">sending messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-rose-600"
            percentage={data.multilingual.sending}
            tooltip={`${data.multilingual.sending} of multilingual sent messages`}
          />
          <div className="text-sm text-black/40">receiving messages</div>
          <ProgressBar
            className="mt-0.5"
            color="bg-violet-700"
            percentage={data.multilingual.receiving}
            tooltip={`${data.multilingual.receiving} of multilingual received messages`}
          />
        </>
      )}
    </Widget>
  )
}

export const MessagesWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const [range, setRange] = React.useState<SimpleDateRange>('month')

  const { data, loading } = useRequest<{
    schoolOut: number
    schoolShared: number
    homeIn: number
    homeOut: number
    schoolName: string
  }>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'messages',
        schoolID,
        data: {
          range: formatDateRange(range),
        },
      }),
      [schoolID, range]
    )
  )

  return (
    <Widget loading={!data || loading}>
      <div className="flex items-center justify-between">
        <div className="font-bold text-black/50">Messages</div>
        <WidgetRangePicker range={range} onRangeChange={setRange} />
      </div>
      {data && (
        <>
          <div className="text-4xl">{data.schoolOut + data.homeOut}</div>
          <div className="flex flex-grow items-center">
            <IconSchool className="mr-1 text-[39px] text-logo" tooltip={data.schoolName} />
            <div className="relative flex-grow">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 500 500"
                preserveAspectRatio="xMidYMid meet"
                className="h-full w-full"
              >
                <defs>
                  <marker
                    id="arrowhead"
                    viewBox="0 0 10 10"
                    refX="3"
                    refY="5"
                    markerWidth="3"
                    markerHeight="6"
                    orient="auto"
                  >
                    <polygon className="fill-[#cfd3d6] stroke-[#cfd3d6]" points="0 0, 10 5, 0 10"></polygon>
                  </marker>
                </defs>
                <path
                  d="M30,175 C150,100 350,100 470,175"
                  className="fill-none stroke-[#cfd3d6] stroke-[10]"
                  style={{ strokeLinecap: 'round' }}
                  markerEnd="url(#arrowhead)"
                />
                <path
                  d="M190,121 C320,110 320,250 180,250"
                  className="fill-none stroke-[#cfd3d6] stroke-[10]"
                  style={{ strokeLinecap: 'round' }}
                />
                <path
                  d="M180,250 C100,250 50,250 30,250"
                  className="fill-none stroke-[#cfd3d6] stroke-[10]"
                  style={{ strokeLinecap: 'round' }}
                  markerEnd="url(#arrowhead)"
                />
                <path
                  d="M470,325 C350,400 150,400 30,325"
                  className="fill-none stroke-[#cfd3d6] stroke-[10]"
                  style={{ strokeLinecap: 'round' }}
                  markerEnd="url(#arrowhead)"
                />
              </svg>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 transform bg-[#f7fbffc2] px-1 font-bold text-black/75"
                style={{ top: '44px', left: '38px', transform: 'translate(-50%, -50%) rotate(348deg)' }}
                data-tippy-content={`${data.schoolOut} outgoing messages from ${data.schoolName}`}
              >
                {data.schoolOut}
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 transform bg-[#f7fbffc2] px-1 font-bold text-black/75"
                style={{ top: '123px', left: '80px' }}
                data-tippy-content={`${data.homeOut} messages sent to ${data.schoolName} by families`}
              >
                {data.homeOut}
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 transform bg-[#f7fbffc2] px-1 font-bold text-black/75"
                style={{ top: '80px', left: '40px' }}
                data-tippy-content={`${data.schoolShared} shared incoming messages to ${data.schoolName}`}
              >
                {data.schoolShared}
              </div>
              <div
                className="absolute -translate-x-1/2 -translate-y-1/2 transform bg-[#f7fbffc2] px-1 font-bold text-black/75"
                style={{ top: '41px', left: '114px', transform: 'translate(-50%, -50%) rotate(14deg)' }}
                data-tippy-content={`${data.homeIn} messages sent to your families`}
              >
                {data.homeIn}
              </div>
            </div>
            <IconHome className="text-[39px] text-logo" tooltip="Students and Guardians" />
          </div>
        </>
      )}
    </Widget>
  )
}

export const ContactMethodsWidget: React.FC = () => {
  const { data, loading } = useRequest<{
    editedContacts: number
    noContactableGuardians: number
    bouncedContacts: number
  }>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'contacts',
      }),
      []
    )
  )

  return (
    <Widget loading={!data || loading}>
      <div className="font-bold text-black/50">Contact Methods</div>
      <div className="mb-2 mt-1 text-black/50">Edited Contacts</div>
      <Link
        to="/contacts/report"
        className="mb-2 flex items-center justify-between rounded bg-warning px-3 py-2 no-underline"
      >
        Edited contact info
        {data && <Badge badgeStyle={BadgeStyle.logo}>{data.editedContacts}</Badge>}
      </Link>
      <div className="my-2 text-black/50">Unreachable Contacts</div>
      <Link
        to="/contacts/report"
        className="mb-2 flex items-center justify-between rounded bg-warning px-3 py-2 no-underline"
      >
        <div>
          Students without
          <br />
          reachable guardians
        </div>
        {data && <Badge badgeStyle={BadgeStyle.logo}>{data.noContactableGuardians}</Badge>}
      </Link>
      <Link
        to="/contacts/report"
        className="mb-2 flex items-center justify-between rounded bg-warning px-3 py-2 no-underline"
      >
        Invalid contact info
        {data && <Badge badgeStyle={BadgeStyle.logo}>{data.bouncedContacts}</Badge>}
      </Link>
    </Widget>
  )
}

export const LanguagesWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const [range, setRange] = React.useState<SimpleDateRange>('month')

  const { data, loading } = useRequest<{ name: string; percent: string; total: number }[]>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'languages',
        schoolID,
        data: {
          range: formatDateRange(range),
        },
      }),
      [schoolID, range]
    )
  )

  return (
    <Widget loading={!data || loading} className="bg-white">
      <div className="flex items-center justify-between">
        <div className="font-bold text-black/50">Top Languages</div>
        <WidgetRangePicker range={range} onRangeChange={setRange} />
      </div>
      {data && (
        <div className="mt-1 flex flex-col gap-1">
          {data.map((lang) => (
            <div className="flex items-center justify-between" key={lang.name}>
              <div className="min-w-0 overflow-hidden text-ellipsis">{lang.name}</div>
              <ProgressBar
                className="ml-2 min-w-40 max-w-40"
                color="bg-sky-400"
                percentage={lang.percent}
                tooltip={`${lang.total} messages were translated to or from ${lang.name}`}
              />
            </div>
          ))}
          {Array.from({ length: Math.max(0, 9 - data.length) }).map((_, i) => (
            <div key={i} className="flex items-center justify-between">
              <div className="my-1 h-4 flex-grow rounded bg-gray-50"></div>
              <ProgressBar className="ml-2 max-w-40 !bg-gray-50" percentage={0} />
            </div>
          ))}
        </div>
      )}
    </Widget>
  )
}

export const ExportWidget: React.FC<{ className?: string }> = ({ className }) => {
  const { state } = useAppContext()
  const user = state.user
  assert(user && user.type === 'teacher', 'Only teachers can use export widget')
  const [range, setRange] = React.useState<DateRange | undefined>(undefined)
  const schoolOptions = [
    ...(user.districtAccess ? [{ value: 'district', name: 'District' }] : []),
    ...(user.districtAccess ? user.district.schools : user.schools).map((s) => ({ value: s.id, name: s.name })),
  ]
  const [school, setSchool] = React.useState(schoolOptions[0])
  const [filter, setFilter] = React.useState('')
  const [loading, setLoading] = React.useState(false)

  return (
    <Widget className={`bg-charcoal text-charcoal-text ${className}`} loading={loading}>
      <div className="font-bold">Message Export</div>
      <div className="my-1">Export Range</div>
      <PopupDateRangePicker includeLongRanges range={range} onRangeChange={setRange}>
        <Button buttonStyle={ButtonStyle.charcoalActive} className="justify-between text-sm">
          {range ? `${range.from?.toDateString()} - ${range.to?.toDateString()}` : 'All Time'}
          {range && (
            <Button
              useDivAsButton
              onClick={(e) => {
                setRange(undefined)
                e.stopPropagation()
              }}
              buttonStyle="hover:bg-charcoal-light"
              shape={ButtonShape.iconSmall}
              className="-my-1 -mr-3 ml-2"
            >
              <IconTimes />
            </Button>
          )}
        </Button>
      </PopupDateRangePicker>
      <div className="my-1">School</div>
      <Select className={CharcoalInput} options={schoolOptions} value={school} onChange={setSchool} />
      <div className="my-1">Filter by email, phone number, or sync ID</div>
      <TextInput
        className={CharcoalInput}
        placeholder="email, phone number, or sync ID"
        value={filter}
        onChange={setFilter}
      />
      <Button
        className="mt-auto"
        logo
        disabled={loading}
        onClick={() => {
          setLoading(true)
          requestBlob('/frontend_api/csvExport', {
            school: school.value,
            filter,
            range: range ? formatDateRange(range) : 'all',
          })
            .then((res) => downloadFile(res, `${school.name}_export.csv`))
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      >
        Download Exported Messages
      </Button>
    </Widget>
  )
}

export const UsageChartWidget: React.FC<{ schoolID?: number }> = ({ schoolID }) => {
  const [range, setRange] = React.useState<SimpleDateRange>('month')
  const [groupAnnouncements, setGroupAnnouncements] = React.useState(false)

  const { data, loading } = useRequest<LineChartData>(
    '/frontend_api/getDashboardWidget',
    React.useMemo(
      () => ({
        widget: 'usage',
        schoolID,
        data: {
          range: formatDateRange(range),
          groupAnnouncements,
        },
      }),
      [schoolID, range, groupAnnouncements]
    )
  )

  return (
    <Widget size={[3, 1.5]} loading={!data || loading}>
      <div className="flex items-center">
        <div className="ml-3 font-bold text-black/50">Usage</div>
        <WidgetRangePicker range={range} onRangeChange={setRange} />
        <LabeledCheckbox
          label="group announcements"
          labelBefore
          className="ml-auto text-black/50"
          checked={groupAnnouncements}
          onChecked={setGroupAnnouncements}
        />
      </div>
      {data && <LineChart data={data} legend className="flex-grow" />}
    </Widget>
  )
}
